// Roamic header dropdowns: live destination search, drawer accordions and featured carousel.

// Plain elements with role="button" are used instead of <button>/<summary> on purpose:
// Dawn's MenuDrawer binds every <summary> and <button> inside <header-drawer> and would
// open its sliding submenu or close the whole drawer on click.
function roamicToggle(toggle) {
  const container = toggle.closest('[data-roamic-region], .roamic-drawer-item');
  const isOpen = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isOpen));
  container?.classList.toggle('is-open', !isOpen);
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-roamic-toggle]');
  if (toggle) roamicToggle(toggle);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const toggle = event.target.closest('[data-roamic-toggle]');
  if (!toggle || event.target !== toggle) return;
  event.preventDefault();
  roamicToggle(toggle);
});

const roamicNormalize = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

if (!customElements.get('roamic-destinations')) {
  customElements.define(
    'roamic-destinations',
    class RoamicDestinations extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-roamic-search]');
        this.regions = [...this.querySelectorAll('[data-roamic-region]')];
        this.empty = this.querySelector('[data-roamic-empty]');
        this.results = this.querySelector('[data-roamic-results]');
        this.input?.addEventListener('input', () => this.onInput());
      }

      // Searches the whole catalog via predictive search + Section Rendering API.
      // Falls back to filtering the menu links (filter) if the request fails.
      onInput() {
        clearTimeout(this.debounceTimer);
        const query = this.input.value.trim();

        if (query === '') {
          this.requestId = (this.requestId || 0) + 1;
          this.abortController?.abort();
          this.setLoading(false);
          this.clearResults();
          this.classList.remove('is-fallback');
          this.filter();
          return;
        }

        if (!this.results || !window.routes?.predictive_search_url) {
          this.fallback();
          return;
        }

        // Immediate feedback; only the latest request clears it (see search).
        this.setLoading(true);
        this.debounceTimer = setTimeout(() => this.search(query), 250);
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        if (!this.results) return;
        if (isLoading) {
          this.results.setAttribute('aria-busy', 'true');
        } else {
          this.results.removeAttribute('aria-busy');
        }
      }

      fallback() {
        this.clearResults();
        this.classList.add('is-fallback');
        this.filter();
      }

      clearResults() {
        if (this.results) {
          this.results.replaceChildren();
          this.results.hidden = true;
        }
        this.classList.remove('has-results');
      }

      async search(query) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestId = (this.requestId = (this.requestId || 0) + 1);
        this.classList.add('is-searching');

        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(
          query
        )}&resources[type]=product&resources[limit]=10&section_id=roamic-destination-results`;

        try {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (requestId !== this.requestId) return;
          const list = html.querySelector('.roamic-results');
          if (!list) throw new Error('Predictive search returned no results markup');
          // The user kept typing: a newer debounced request will clear the loading state.
          if (this.input.value.trim() !== query) return;

          // Predictive search also returns semantic matches (e.g. "spain" -> other European
          // countries). When some results contain the query in their title, show only those;
          // otherwise keep Shopify's semantic results unchanged.
          const q = roamicNormalize(query);
          const items = [...list.querySelectorAll('[data-roamic-result]')];
          const matches = items.filter((li) => roamicNormalize(li.textContent.trim()).includes(q));
          if (matches.length) {
            items.filter((li) => !matches.includes(li)).forEach((li) => li.remove());
          }

          this.results.replaceChildren(list);
          this.results.hidden = false;
          this.classList.remove('is-fallback');
          this.classList.add('has-results');
          if (this.empty) this.empty.hidden = true;
          this.setLoading(false);
        } catch (error) {
          if (error.name === 'AbortError' || requestId !== this.requestId) return;
          this.setLoading(false);
          this.fallback();
        }
      }

      filter() {
        const query = roamicNormalize(this.input.value);
        let matches = 0;

        this.regions.forEach((region) => {
          const regionTitle = roamicNormalize(region.querySelector('.roamic-region__title')?.textContent || '');
          const regionMatches = query !== '' && regionTitle.includes(query);
          let visible = 0;

          region.querySelectorAll('[data-roamic-item]').forEach((item) => {
            // "View all" links only show when not searching
            if (item.hasAttribute('data-roamic-view-all')) {
              item.hidden = query !== '';
              return;
            }
            const show = query === '' || regionMatches || roamicNormalize(item.dataset.name).includes(query);
            item.hidden = !show;
            if (show) visible++;
          });

          region.hidden = query !== '' && visible === 0;
          matches += visible;
        });

        this.classList.toggle('is-searching', query !== '');
        if (this.empty) this.empty.hidden = matches > 0;
      }
    }
  );
}

if (!customElements.get('roamic-featured')) {
  customElements.define(
    'roamic-featured',
    class RoamicFeatured extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-roamic-slide]')];
        this.dots = [...this.querySelectorAll('[data-roamic-dot]')];
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.show(Number(dot.dataset.roamicDot)));
          dot.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.show(Number(dot.dataset.roamicDot));
          });
        });
      }

      show(index) {
        this.slides.forEach((slide, i) => (slide.hidden = i !== index));
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }
    }
  );
}
